import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import { ButtonPrimary, ButtonSecondary } from "@/components/Buttons";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";
import { ClipboardCheck, CreditCard, Loader2, TriangleAlert } from "lucide-react";

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

export default function CheckoutSuccess() {
    const [params] = useSearchParams();
    const orderId = params.get("order");
    const sessionId = params.get("session_id");
    const { clear } = useCart();
    const [state, setState] = useState({ phase: "loading" });
    const [payment, setPayment] = useState(sessionId ? "confirming" : null); // confirming | paid | failed
    const ran = useRef(false);

    // Stripe return: confirm the payment with the backend before showing the paid state
    useEffect(() => {
        if (!sessionId) return;
        let cancelled = false;
        let tries = 0;
        const poll = async () => {
            while (!cancelled && tries < 15) {
                tries += 1;
                try {
                    const res = await api.get(`/payments/status/${sessionId}`);
                    if (res.data.payment_status === "paid") {
                        if (!cancelled) setPayment("paid");
                        clear(); // payment confirmed — safe to empty the cart now
                        return;
                    }
                } catch { /* keep polling */ }
                await new Promise((r) => setTimeout(r, 2000));
            }
            if (!cancelled) setPayment("failed");
        };
        poll();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    useEffect(() => {
        if (!orderId || ran.current) return;
        if (sessionId && payment === "confirming") return; // wait for Stripe confirmation first
        ran.current = true;
        Promise.all([api.get("/orders/mine"), api.get("/checkout/config")])
            .then(([ordersRes, configRes]) => {
                const order = ordersRes.data.find((o) => o.order_id === orderId);
                const method = (configRes.data.payment_methods || []).find((m) => m.key === order?.payment_method);
                setState(order ? { phase: "found", order, method } : { phase: "error" });
            })
            .catch(() => setState({ phase: "error" }));
    }, [orderId, sessionId, payment]);

    let content;
    if (payment === "failed") {
        content = (
            <div className="max-w-xl mx-auto border border-stone-200 bg-white py-14 px-8 text-center" data-testid="payment-failed">
                <TriangleAlert className="h-10 w-10 text-wine mx-auto" strokeWidth={1.25} />
                <h2 className="mt-6 font-serif text-3xl font-light text-brand">We couldn&apos;t confirm your payment</h2>
                <p className="mt-4 text-sm text-mist leading-relaxed">
                    No completed charge was found. If your payment went through, it may still be processing —
                    check My Account in a few minutes, or contact hello@grayhavenresearch.com.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
                    <ButtonPrimary to="/account" data-testid="failed-account-btn">My Account</ButtonPrimary>
                    <ButtonSecondary to="/checkout" data-testid="failed-checkout-btn">Back to Checkout</ButtonSecondary>
                </div>
            </div>
        );
    } else if (state.phase === "loading" || payment === "confirming") {
        content = (
            <div className="max-w-xl mx-auto border border-stone-200 bg-white py-16 px-8 text-center" data-testid="payment-confirming">
                <Loader2 className="h-8 w-8 animate-spin text-gold mx-auto" />
                <p className="mt-6 text-sm text-mist">
                    {payment === "confirming" ? "Confirming your payment with Stripe…" : "Loading your order…"}
                </p>
            </div>
        );
    } else if (state.phase === "found") {
        const o = state.order;
        const isPaid = o.status === "paid";
        content = (
            <div className="max-w-2xl mx-auto border border-stone-200 bg-white py-14 px-8 sm:px-12 text-center" data-testid="order-confirmation">
                {isPaid ? (
                    <CreditCard className="h-10 w-10 text-wine mx-auto" strokeWidth={1.25} data-testid="order-paid-icon" />
                ) : (
                    <ClipboardCheck className="h-10 w-10 text-wine mx-auto" strokeWidth={1.25} />
                )}
                <h2 className="mt-6 font-serif text-3xl font-light text-brand">
                    {isPaid ? "Payment confirmed" : "Order received"}
                </h2>
                <p className="mt-4 text-sm text-mist leading-relaxed">
                    Order <span className="font-semibold text-brand" data-testid="order-confirmation-id">{o.order_id}</span>{" "}
                    (total <span data-testid="order-confirmation-total">{money(o.total_cents)}</span>)
                    {isPaid
                        ? <> is <span className="text-wine font-semibold">paid in full</span>. A confirmation is on its way to {o.email}.</>
                        : <> has been placed and is <span className="text-wine font-semibold">awaiting review</span>. No payment has been taken.</>}
                </p>
                {!isPaid && (
                <div className="mt-8 bg-surface2 border border-gold/25 p-6 text-left">
                    <p className="text-[0.6rem] uppercase tracking-[0.22em] text-slate-500 font-semibold">What happens next</p>
                    {state.method && !state.method.is_payment_link && state.method.link_url ? (
                        <div className="mt-3 space-y-3">
                            <p className="text-xs leading-relaxed text-slate-600" data-testid="order-method-instructions">
                                {state.method.instructions}
                            </p>
                            <a
                                href={state.method.link_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="order-pay-external-btn"
                                className="inline-flex items-center justify-center bg-wine text-white px-8 py-3 text-[0.7rem] uppercase tracking-[0.2em] font-semibold hover:bg-wine-deep transition-colors duration-300"
                            >
                                Pay {money(o.total_cents)} with {state.method.label}
                            </a>
                            <p className="text-[0.65rem] text-slate-400">
                                Include your order number ({o.order_id}) in the payment note. Your order is marked
                                paid only after our team confirms your payment — paying now does not change the
                                order status automatically.
                            </p>
                        </div>
                    ) : (
                        <ol className="mt-3 space-y-2.5 text-xs leading-relaxed text-slate-600 list-decimal list-inside">
                            <li>Our team reviews your order.</li>
                            <li>We email your payment link to {o.email} — nothing is charged automatically.</li>
                            <li>You complete payment separately through the link.</li>
                            <li>Your order is marked paid only after payment is confirmed by our team.</li>
                        </ol>
                    )}
                </div>
                )}
                {isPaid && (
                    <p className="mt-8 text-xs leading-relaxed text-slate-500 max-w-md mx-auto" data-testid="order-paid-note">
                        Your payment was processed securely by Stripe. Our team will prepare your order and reach
                        out if anything is needed.
                    </p>
                )}
                <p className="mt-6 text-[0.65rem] leading-relaxed text-slate-400 max-w-md mx-auto">
                    All materials are supplied strictly for laboratory research use only. Not for human or
                    veterinary use.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
                    <ButtonPrimary to="/materials" data-testid="success-browse-btn">
                        Continue Browsing
                    </ButtonPrimary>
                    <ButtonSecondary to="/account" data-testid="success-account-btn">
                        My Account
                    </ButtonSecondary>
                </div>
            </div>
        );
    } else {
        content = (
            <div className="max-w-xl mx-auto border border-stone-200 bg-white py-14 px-8 text-center" data-testid="order-confirmation-error">
                <TriangleAlert className="h-10 w-10 text-wine mx-auto" strokeWidth={1.25} />
                <h2 className="mt-6 font-serif text-3xl font-light text-brand">We couldn&apos;t find this order</h2>
                <p className="mt-4 text-sm text-mist leading-relaxed">
                    If you just placed an order it is safe — check My Account or contact
                    hello@grayhavenresearch.com.
                </p>
            </div>
        );
    }

    return (
        <div data-testid="checkout-success-page">
            <PageHeader eyebrow="Order Placed" title="Order Confirmation" />
            <section className="pb-24 sm:pb-32 px-6 lg:px-8">
                <Reveal>{content}</Reveal>
            </section>
        </div>
    );
}
